import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import { reportsApi, branchesApi, companiesApi } from "@/lib/api";
import { toast } from "sonner";
import { Wallet, Search, Building2, Briefcase } from "lucide-react";

export default function Arqueo() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const getLocalDate = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    return local.toISOString().split("T")[0];
  };
  const [startDate, setStartDate] = useState(getLocalDate());
  const [endDate, setEndDate] = useState(getLocalDate());
  const [selectedBranch, setSelectedBranch] = useState("all");
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [branches, setBranches] = useState([]);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      const [branchesRes, companiesRes] = await Promise.all([
        branchesApi.getAll(),
        companiesApi.getAll(),
      ]);
      setBranches(branchesRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      console.error("Error loading filters:", error);
    }
  };

  const fetchArqueo = async () => {
    setLoading(true);
    try {
      const response = await reportsApi.getArqueo(
        startDate || undefined,
        endDate || undefined,
        selectedBranch || undefined,
        selectedCompany || undefined
      );
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar arqueo");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArqueo();
  }, []);

  return (
    <div className="space-y-6" data-testid="arqueo-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
          Arqueo
        </h1>
        <p className="text-muted-foreground">
          Resumen de ingresos y órdenes por período
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Wallet className="h-5 w-5" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label>Sucursal</Label>
              <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                <SelectTrigger className="w-48" data-testid="branch-select">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Seleccionar sucursal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las sucursales</SelectItem>
                  {branches.map((branch) => (
                    <SelectItem key={typeof branch === 'string' ? branch : branch.id} value={typeof branch === 'string' ? branch : branch.name}>
                      {typeof branch === 'string' ? branch : branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Empresa</Label>
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger className="w-48" data-testid="company-select">
                  <Briefcase className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Seleccionar empresa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las empresas</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fecha Inicio</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha Fin</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="end-date"
              />
            </div>
            <Button onClick={fetchArqueo} disabled={loading} data-testid="search-arqueo-btn">
              <Search className="h-4 w-4 mr-2" />
              {loading ? "Cargando..." : "Consultar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {data && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Detalle de Órdenes ({data.total_orders})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.orders?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No hay órdenes para este período
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Folio</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead className="text-center">No. Estudios</TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead className="text-right">Abono</TableHead>
                      <TableHead className="text-right">Adeudo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.orders?.map((order, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-mono font-medium">
                          {order.folio}
                        </TableCell>
                        <TableCell>{order.patient_name}</TableCell>
                        <TableCell className="text-center">
                          {order.studies_count}
                        </TableCell>
                        <TableCell>{order.company_name || "Particular"}</TableCell>
                        <TableCell>{order.date}</TableCell>
                        <TableCell className="text-right text-emerald-600 font-medium">
                          ${order.amount_paid.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-orange-600 font-medium">
                          ${order.pending_balance.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow className="bg-muted/80 font-bold">
                      <TableCell colSpan={5} className="text-right">
                        TOTALES:
                      </TableCell>
                      <TableCell className="text-right text-emerald-700 text-lg">
                        ${data.total_paid?.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-orange-700 text-lg">
                        ${data.total_pending?.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
